import {
  asCurrency,
  getMonthlyPayment,
  getAdjustableMortgageAmortization,
  normalizedDate,
  updateValuesFromFields,
  validateFields,
} from '../util'
import { useAppState } from '../Context'
import { EquityBalanceInterestChart } from '../components/Charts'
import { AmortizationScheduleTable } from '../components/Tables'
import { LoanCompareFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'
import addMonths from 'date-fns/addMonths'
import currency from 'currency.js'

function PanelInput() {
  return <LoanCompareFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  const pointsCost1 = currency(values.amount)
    .subtract(values.downPayment1)
    .multiply(values.points1 / 100)
  const originationCosts1 = currency(values.fees1).add(pointsCost1)
  const pointsAmount1 = currency(values.amount).multiply(values.points1 / 15000)

  const pointsCost2 = currency(values.amount)
    .subtract(values.downPayment2)
    .multiply(values.points2 / 100)
  const originationCosts2 = currency(values.fees2).add(pointsCost2)
  const pointsAmount2 = currency(values.amount).multiply(values.points2 / 15000)

  const originationCostsDiff = originationCosts1.subtract(originationCosts2)

  const numPayments1 = values.numPayments1
  const numPayments2 = values.numPayments2
  const numPaymentsDiff = numPayments1 - numPayments2

  const totalTaxedInterest1 = values.amortization1.totalInterest.multiply(values.taxRate / 100)
  const totalLoanCost1 = values.amortization1.totalPayment
    .add(originationCosts1)
    .subtract(totalTaxedInterest1)
    .subtract(pointsAmount1)

  const totalTaxedInterest2 = values.amortization2.totalInterest.multiply(values.taxRate / 100)
  const totalLoanCost2 = values.amortization2.totalPayment
    .add(originationCosts2)
    .subtract(totalTaxedInterest2)
    .subtract(pointsAmount2)

  const totalSavings = totalLoanCost1.subtract(totalLoanCost2)

  return (
    <CalculateResults>
      <CalculateRow>
        <div>Loan Amount:</div>
        <div>{asCurrency(values.amount)}</div>
      </CalculateRow>

      <table className="w-full text-xs md:text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th></th>
            <th className="py-3 px-2 text-right">Loan 1</th>
            <th className="py-3 px-2 text-right">- Loan 2</th>
            <th className="py-3 px-2 text-right">= Difference</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-300">
          <tr>
            <td className="py-3 px-2">Monthly Payment:</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.monthlyPayment1)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.monthlyPayment2)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.monthlySavings)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Origination Cost:</td>
            <td className="py-3 px-2 text-right">{asCurrency(originationCosts1)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(originationCosts2)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(originationCostsDiff)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Loan Term (Months):</td>
            <td className="py-3 px-2 text-right">{numPayments1}</td>
            <td className="py-3 px-2 text-right">{numPayments2}</td>
            <td className="py-3 px-2 text-right">{numPaymentsDiff}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">
              Total Loan Cost<sup>*</sup>:
            </td>
            <td className="py-3 px-2 text-right">{asCurrency(totalLoanCost1)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalLoanCost2)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalSavings)}</td>
          </tr>
        </tbody>
      </table>

      <CalculateRow className="p-4">
        <div>
          <strong>* Note:</strong> The total loan cost figure is actually the present value of all the payments for each
          loan, using the investment rate of return as a discount rate.
        </div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <EquityBalanceInterestChart amortization={values.amortization1} title="Loan 1" />
      <EquityBalanceInterestChart amortization={values.amortization2} title="Loan 2" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return (
    <>
      <div className="p-3 font-bold">Payment Schedule For Loan 1</div>
      <AmortizationScheduleTable amortization={values.amortization1} />
      <div className="p-3 font-bold">Payment Schedule For Loan 2</div>
      <AmortizationScheduleTable amortization={values.amortization2} />
    </>
  )
}

export default {
  name: 'Which loan is better, fixed or adjustable?',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)

          const principal1 = currency(updatedValues.amount).subtract(updatedValues.downPayment1)
          const monthlyPayment1 = getMonthlyPayment(principal1, updatedValues.interestRate1, updatedValues.numPayments1)

          const amortization1 = getAdjustableMortgageAmortization({
            principal: principal1,
            interestRate: updatedValues.interestRate1,
            numPayments: updatedValues.numPayments1,
            monthlyPayment: monthlyPayment1,
            startDate: addMonths(normalizedDate(new Date()), 1),
            rateCap: updatedValues.rateCap1,
            maxFirstRate: 0,
            maxRate: updatedValues.repricingRate1,
            numMonthsBefore: 0,
            numMonthsBetween: updatedValues.repricingMonths1,
          })

          const principal2 = currency(updatedValues.amount).subtract(updatedValues.downPayment2)
          const monthlyPayment2 = getMonthlyPayment(principal2, updatedValues.interestRate2, updatedValues.numPayments2)

          const amortization2 = getAdjustableMortgageAmortization({
            principal: principal2,
            interestRate: updatedValues.interestRate2,
            numPayments: updatedValues.numPayments2,
            monthlyPayment: monthlyPayment2,
            startDate: addMonths(normalizedDate(new Date()), 1),
            rateCap: updatedValues.rateCap2,
            maxFirstRate: 0,
            maxRate: updatedValues.repricingRate2,
            numMonthsBefore: 0,
            numMonthsBetween: updatedValues.repricingMonths2,
          })

          const monthlySavings = monthlyPayment1.subtract(monthlyPayment2)

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'monthlyPayment1', value: monthlyPayment1 } })
          dispatch({ type: 'setValue', payload: { name: 'monthlyPayment2', value: monthlyPayment2 } })
          dispatch({ type: 'setValue', payload: { name: 'monthlySavings', value: monthlySavings } })
          dispatch({ type: 'setValue', payload: { name: 'amortization1', value: amortization1 } })
          dispatch({ type: 'setValue', payload: { name: 'amortization2', value: amortization2 } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'amount',
      label: 'Loan Amount',
      value: 150000,
      singleRow: true,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'taxRate',
      label: 'Your Income Tax Rate',
      value: 28,
      singleRow: true,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'downPayment1',
      label: 'Down Payment',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'downPayment2',
      label: 'Down Payment',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments1',
      label: 'Number of Payments',
      value: 360,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments2',
      label: 'Number of Payments',
      value: 360,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate1',
      label: 'Interest Rate',
      value: 8.75,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate2',
      label: 'Interest Rate',
      value: 9.25,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'points1',
      label: 'Points',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'points2',
      label: 'Points',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'fees1',
      label: 'Fees',
      value: 800,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'fees2',
      label: 'Fees',
      value: 400,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'repricingMonths1',
      label: 'Re-Pricing Interval (Months)',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'repricingMonths2',
      label: 'Re-Pricing Interval (Months)',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'repricingRate1',
      label: 'Re-Pricing Rate Adjustment',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'repricingRate2',
      label: 'Re-Pricing Rate Adjustment',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'rateCap1',
      label: 'Anticipated Rate Cap',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'rateCap2',
      label: 'Anticipated Rate Cap',
      value: 0,
    },
  ],
}
